import axios, { type AxiosInstance, type AxiosRequestConfig } from 'axios'
import qs from 'qs'

// Note: REFRESH_TOKEN_URL is imported from authentication.service
// We'll need to export it from there
export let REFRESH_TOKEN_URL = ''

// Helper to set the refresh token URL after authentication service is loaded
export function setRefreshTokenURL(url: string) {
  REFRESH_TOKEN_URL = url
}

export interface IBaseAPIService {
  setJWT(jwt: string): void
  unsetJWT(): void
  client: AxiosInstance
}

export class BaseAPIService implements IBaseAPIService {
  public readonly client: AxiosInstance

  constructor() {
    this.client = axios.create({
      baseURL: import.meta.env.VITE_API_BASE_URL,
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json',
      },
      paramsSerializer(params) {
        return qs.stringify(params)
      },
    })

    // Request interceptor — inject JWT from localStorage on every request.
    // Source of truth: localStorage 'auth_token' (auth store hydrates from it).
    // Required because each service instantiates its own BaseAPIService;
    // only AuthenticationService's setJWT() touches its own client.defaults.
    this.client.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('auth_token')
        if (token) {
          config.headers = config.headers || {}
          config.headers['Authorization'] = `Bearer ${token}`
        }
        return config
      },
      (error) => {
        console.error('base api service request error')
        console.error(error)

        return Promise.reject(error)
      },
    )

    // Response interceptor with 401 handling and token refresh
    this.client.interceptors.response.use(
      (response) => {
        // Any status code within the range of 2xx triggers this function
        // Uncomment for debugging:
        // console.log('response', response);

        return response
      },
      async (error) => {
        // Any status code outside the range of 2xx triggers this function
        const originalRequest: AxiosRequestConfig & {
          _retry?: boolean
        } = error.config

        if (error.response?.status >= 400 && error.response?.status <= 499) {
          // Handle individual error cases in this block

          // The refresh endpoint's own failure never navigates, and never
          // re-enters the refresh/retry path below. Redirects belong to the
          // navigation guard: this rejection is usually being awaited by an
          // in-flight guard (guards.ts:29 -> auth.ts restoreSession ->
          // refreshToken), and navigating from here made that guard wait on
          // itself — a deadlock, not an error, so nothing was ever thrown and
          // the app rendered blank. The bare rethrow is load-bearing for a
          // second reason: this POST is issued from inside the refreshInFlight
          // latch (auth.ts:208-210), so falling through to the 401 retry below
          // would await the very promise that cannot settle until we return.
          if (originalRequest.url === REFRESH_TOKEN_URL) {
            throw error
          }

          // Handle 403 Forbidden
          if (error.response?.status === 403) {
            error.response.data = {
              errors: ['Forbidden.'],
            }
            throw error
          }

          // Don't throw error here for other 4xx codes
        }

        // Handle 401 Unauthorized with automatic token refresh
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true

          // Dynamic import to avoid circular dependency
          // Auth store imports this service, so we can't import it at the top level
          const { useAuthStore } = await import('@/stores/auth')
          const authStore = useAuthStore()

          // Only try to refresh if user was authenticated (has token)
          // For public users (no token), let the 401 propagate as an error
          if (!authStore.isAuthenticated) {
            throw error
          }

          // Try to refresh the token
          await authStore.refreshToken()

          // Retry the original request with the new token
          originalRequest.headers = originalRequest.headers || {}
          originalRequest.headers['Authorization'] = `Bearer ${authStore.token}`

          return await this.client(originalRequest)
        }

        throw error
      },
    )
  }

  public setJWT(jwt: string): void {
    this.client.defaults.headers['Authorization'] = 'Bearer ' + jwt
  }

  public unsetJWT(): void {
    if (this.client.defaults.headers['Authorization']) {
      delete this.client.defaults.headers['Authorization']
    }
  }
}
